import {
  activateAgent,
  createAgent,
  createRcsSettings,
  deleteRcsSettings,
  getAgent,
  getAgentList,
  getCTAEngagement,
  getCampaignKpi,
  getRcsSettings,
  getRcsSettingsList,
  getStatsCTAEngagement,
  getStatsDeliverability,
  getStatsEngagement,
  getStatsUnsubscription,
  searchAdresse,
  updateRcsSettings
} from "./chunk.DJW5UPIU.js";

// src/lib/hooks/agents/use-create-agent.ts
import { useToastContext } from "@oppy/omniv2-ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
function useCreateAgent() {
  const queryClient = useQueryClient();
  const { mutate, ...mutationFlags } = useMutation({
    mutationFn: createAgent
  });
  const { showMessage } = useToastContext();
  const createAgent2 = ({
    data,
    entityId,
    onSuccessCallback
  }) => {
    mutate(
      { entityId, data },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["rcsAgents", entityId]
          });
          showMessage({
            severity: "success",
            detail: "Votre agent RCS a bien \xE9t\xE9 ajout\xE9"
          });
          onSuccessCallback();
        }
      }
    );
  };
  return { createAgent: createAgent2, ...mutationFlags };
}

// src/lib/hooks/agents/use-suspense-get-agent.ts
import { useToastContext as useToastContext2 } from "@oppy/omniv2-ui";
import { useSuspenseQuery } from "@tanstack/react-query";
var denormalizeAgent = function(rcsAgent) {
  const { logoUrl, bannerUrl, ...rest } = rcsAgent;
  return {
    ...rest,
    logo: {
      src: logoUrl
    },
    banner: {
      src: bannerUrl
    }
  };
};
function useSuspenseGetAgent({
  entityId,
  rcsAgentId,
  onErrorCallback
}) {
  const { showMessage } = useToastContext2();
  const { data, error, isFetching } = useSuspenseQuery({
    queryKey: ["rcsAgent", entityId, rcsAgentId],
    queryFn: () => getAgent({ entityId, rcsAgentId }),
    select: denormalizeAgent
  });
  if (error && !isFetching) {
    showMessage({
      severity: "error",
      summary: "Erreur...",
      detail: "Impossible d'identifier l'agent RCS \xE0 consulter"
    });
    onErrorCallback?.();
  }
  return { data };
}

// src/lib/hooks/agents/use-activate-agent.ts
import { useToastContext as useToastContext3 } from "@oppy/omniv2-ui";
import { useMutation as useMutation2, useQueryClient as useQueryClient2 } from "@tanstack/react-query";
function useActivateAgent() {
  const queryClient = useQueryClient2();
  const { mutate } = useMutation2({
    mutationFn: activateAgent
  });
  const { showMessage } = useToastContext3();
  return ({ agentId, entityId }) => {
    mutate(
      { agentId, entityId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["rcsAgents", entityId]
          });
          showMessage({
            severity: "success",
            detail: "Votre agent RCS a bien \xE9t\xE9 activ\xE9"
          });
        }
      }
    );
  };
}

// src/lib/hooks/agents/use-get-agent-list.ts
import { useQuery } from "@tanstack/react-query";
function useGetAgentList({ entityId, searchParams }) {
  return useQuery({
    queryKey: ["rcsAgents", entityId, searchParams.toString()],
    queryFn: () => getAgentList({ entityId, searchParams })
  });
}

// src/lib/hooks/stats/use-get-campaign-stats-dashboard.ts
import { useSuspenseQuery as useSuspenseQuery2 } from "@tanstack/react-query";
function useGetCampaignStatistics({
  entityId,
  campaignId
}) {
  return useSuspenseQuery2({
    queryKey: ["rcsCampaignStatistics", entityId, campaignId],
    queryFn: () => getCampaignKpi({
      entityId,
      campaignId
    })
  });
}

// src/lib/hooks/stats/use-get-stats-deliverability.ts
import { useSuspenseQuery as useSuspenseQuery3 } from "@tanstack/react-query";
function useGetStatsDeliverability({
  entityId,
  campaignId,
  searchParams
}) {
  return useSuspenseQuery3({
    queryKey: [
      "rcsDeliverabilityDetails",
      entityId,
      campaignId,
      searchParams.toString()
    ],
    queryFn: () => getStatsDeliverability({
      entityId,
      campaignId,
      searchParams
    })
  });
}

// src/lib/hooks/stats/use-get-cta-engagement.ts
import { useQuery as useQuery2 } from "@tanstack/react-query";
function useGetCTAEngagement({ entityId, campaignId }) {
  return useQuery2({
    queryKey: ["ctaEngagement", entityId, campaignId],
    queryFn: () => getCTAEngagement({ entityId, campaignId }).then(
      (data) => data.map(({ key, value }) => ({ label: value, value: key }))
    )
  });
}

// src/lib/hooks/stats/use-get-stats-engagement.ts
import { formatTicksToShortLabel } from "@oppy/omniv2-utils";
import { useQuery as useQuery3 } from "@tanstack/react-query";

// src/pages/stats/engagement/rcs-stats-engagement.constants.tsx
import { faCalendarAlt, faPhone } from "@oppy/omniv2-ui";
import { formatDate, formatPhone } from "@oppy/omniv2-utils";
var COLUMNS = [
  {
    field: "phoneNumber",
    header: "Numero",
    body: ({ phoneNumber }) => formatPhone(phoneNumber)
  },
  {
    field: "callToAction",
    header: "Call to action",
    body: ({ callToAction }) => callToAction
  },
  {
    field: "createdAt",
    header: "Date/Heure",
    body: ({ createdAt }) => formatDate(createdAt)
  }
];
var RCS_INTERACTION_STATS = {
  ["ReadingRate" /* READING_RATE */]: {
    icon: faCalendarAlt,
    label: "Taux de lecture",
    value: "0%",
    color: "bg-primary"
  },
  ["EngagementRate" /* ENGAGEMENT_RATE */]: {
    icon: faPhone,
    label: "Taux d'engagement",
    value: "0%",
    color: "bg-green-500"
  },
  ["ReadingDelay" /* READING_DELAY */]: {
    icon: faCalendarAlt,
    label: "D\xE9lai de consultation",
    value: "0min 0sec",
    color: "bg-warning"
  },
  ["EngagementDelay" /* ENGAGEMENT_DELAY */]: {
    icon: faCalendarAlt,
    label: "D\xE9lai d'interaction",
    value: "0sec",
    color: "bg-blue"
  }
};
var RCS_INTERACTION_TIME_STATS = {
  ["ReadingTime" /* READING_TIME */]: {
    title: "Indices de lecture moyens",
    labels: [],
    lineValues: [],
    color: "orange"
  },
  ["EngagementTime" /* ENGAGEMENT_TIME */]: {
    title: "Indices de clics moyens",
    labels: [],
    lineValues: []
  }
};

// src/lib/hooks/stats/use-get-stats-engagement.ts
var getEngagementHeaderStatsValue = (data) => {
  switch (data.key) {
    case "EngagementDelay" /* ENGAGEMENT_DELAY */:
    case "ReadingDelay" /* READING_DELAY */:
      return formatTicksToShortLabel(data.value);
    default:
      return `${data.value}%`;
  }
};
var mapEngagementStats = (data) => {
  const { engagementHeaderStats, engagementStats } = data;
  const mapEngagementHeaderStats = engagementHeaderStats.map(
    (engagementStat) => ({
      ...RCS_INTERACTION_STATS[engagementStat.key],
      value: getEngagementHeaderStatsValue(engagementStat)
    })
  );
  const mapEngagementStats2 = engagementStats.map(({ data: data2, type }) => ({
    ...RCS_INTERACTION_TIME_STATS[type],
    labels: data2.map(({ key }) => key),
    lineValues: data2.map(({ value }) => value)
  }));
  return {
    engagementHeaderStats: mapEngagementHeaderStats,
    engagementStats: mapEngagementStats2
  };
};
function useGetStatsEngagement({ entityId, campaignId }) {
  return useQuery3({
    queryKey: ["rcsEngagement", entityId, campaignId],
    queryFn: () => getStatsEngagement({ entityId, campaignId }).then(
      (data) => mapEngagementStats(data)
    )
  });
}

// src/lib/hooks/stats/use-get-stats-engagement-designer.ts
import { useQuery as useQuery4 } from "@tanstack/react-query";

// src/lib/hooks/stats/use-get-stats-table-engagement.ts
import { useQuery as useQuery5 } from "@tanstack/react-query";
function useGetStatsTableEngagement({
  entityId,
  campaignId,
  searchParams
}) {
  return useQuery5({
    queryKey: [
      "rcsEngagementStats",
      entityId,
      campaignId,
      searchParams.toString()
    ],
    queryFn: () => getStatsCTAEngagement({ entityId, campaignId, searchParams })
  });
}

// src/lib/hooks/stats/use-get-stats-unsubscription.ts
import { useQuery as useQuery6 } from "@tanstack/react-query";
function useGetStatsUnsubscription({
  entityId,
  campaignId,
  searchParams
}) {
  return useQuery6({
    queryKey: ["rcsStop", entityId, campaignId, searchParams.toString()],
    queryFn: () => getStatsUnsubscription({ entityId, campaignId, searchParams })
  });
}

// src/lib/hooks/adresse/use-search-adresse.ts
import { useQueryClient as useQueryClient3 } from "@tanstack/react-query";
import { useCallback } from "react";
var denormalizeAdresse = function({
  features
}) {
  return features.map(
    ({
      geometry: {
        coordinates: [longitude, latitude]
      },
      properties: { id, label }
    }) => ({
      id,
      label,
      latLong: {
        latitude,
        longitude
      }
    })
  );
};
function useSearchAdresse() {
  const queryClient = useQueryClient3();
  const triggerSearchAdresse = useCallback(
    async (q, limit = 15) => {
      const featureCollection = await queryClient.ensureQueryData({
        queryKey: ["adresses", q, limit],
        queryFn: () => searchAdresse({
          searchParams: new URLSearchParams({ q, limit: `${limit}` })
        })
      });
      return denormalizeAdresse(featureCollection);
    },
    [queryClient]
  );
  return triggerSearchAdresse;
}

// src/lib/hooks/rcs-settings/use-create-rcs-settings.ts
import { useToastContext as useToastContext4 } from "@oppy/omniv2-ui";
import { useMutation as useMutation3, useQueryClient as useQueryClient4 } from "@tanstack/react-query";
var normalizeToRcsCreatePayload = function(rcsSettingsType) {
  const { flowDataJson, ...rest } = rcsSettingsType;
  return {
    ...rest,
    content: "{}",
    flowDataJson: JSON.stringify(flowDataJson)
  };
};
function useCreateRcsSettings() {
  const queryClient = useQueryClient4();
  const { mutate, ...mutationFlags } = useMutation3({
    mutationFn: createRcsSettings
  });
  const { showMessage } = useToastContext4();
  const createRcsSettings2 = ({
    entityId,
    rcsSettings,
    onSuccessCallback
  }) => {
    const data = normalizeToRcsCreatePayload(rcsSettings);
    mutate(
      { entityId, data },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["rcsSettings", entityId]
          });
          showMessage({
            severity: "success",
            detail: "Votre contenu RCS a bien \xE9t\xE9 ajout\xE9"
          });
          onSuccessCallback();
        }
      }
    );
  };
  return { createRcsSettings: createRcsSettings2, ...mutationFlags };
}

// src/lib/hooks/rcs-settings/use-delete-rcs-settings.ts
import { useToastContext as useToastContext5 } from "@oppy/omniv2-ui";
import { useMutation as useMutation4, useQueryClient as useQueryClient5 } from "@tanstack/react-query";
function useDeleteRcsSettings() {
  const queryClient = useQueryClient5();
  const { mutate } = useMutation4({
    mutationFn: deleteRcsSettings
  });
  const { showMessage } = useToastContext5();
  return ({ entityId, rcsSettingsId }) => {
    mutate(
      { entityId, rcsSettingsId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["rcsSettings", entityId]
          });
          showMessage({
            severity: "success",
            detail: "Votre contenu RCS a bien \xE9t\xE9 supprim\xE9"
          });
        }
      }
    );
  };
}

// src/lib/hooks/rcs-settings/use-get-rcs-settings-list.ts
import { useQuery as useQuery7 } from "@tanstack/react-query";
function useGetRcsSettingsList({
  entityId,
  searchParams
}) {
  return useQuery7({
    queryKey: ["rcsSettings", entityId, searchParams.toString()],
    queryFn: () => getRcsSettingsList({ entityId, searchParams })
  });
}

// src/lib/hooks/rcs-settings/use-suspense-get-rcs-settings.ts
import { useToastContext as useToastContext6 } from "@oppy/omniv2-ui";
import { useSuspenseQuery as useSuspenseQuery4 } from "@tanstack/react-query";
var mapNodesSuggestions = function(nodes) {
  return nodes.map((node) => ({
    ...node,
    data: {
      ...node.data,
      suggestions: node.data.suggestions?.map(
        (suggestion) => !suggestion?.actionType || ![
          "call",
          "url",
          "reply",
          "createCalendarEventAction",
          "viewLocationAction"
        ].includes(suggestion.actionType) ? { ...suggestion, actionType: "reply" } : suggestion
      ) ?? []
    }
  }));
};
var denormalizeToRcsType = function(rcsSettings) {
  const { flowDataJson, ...rest } = rcsSettings;
  const parsedFlowDataJson = JSON.parse(flowDataJson);
  parsedFlowDataJson.nodes = mapNodesSuggestions(parsedFlowDataJson.nodes);
  return {
    ...rest,
    flowDataJson: parsedFlowDataJson
  };
};
function useSuspenseGetRcsSettings({
  entityId,
  rcsSettingsId,
  onErrorCallback
}) {
  const { showMessage } = useToastContext6();
  const { data, error, isFetching } = useSuspenseQuery4({
    queryKey: ["rcsSettings", entityId, rcsSettingsId],
    queryFn: () => getRcsSettings({ entityId, rcsSettingsId }),
    staleTime: 0,
    select: denormalizeToRcsType
  });
  if (error && !isFetching) {
    showMessage({
      severity: "error",
      summary: "Erreur...",
      detail: "Impossible d'identifier le RCS \xE0 modifier"
    });
    onErrorCallback?.();
  }
  return { data };
}

// src/lib/hooks/rcs-settings/use-update-rcs-settings.ts
import { useEntity } from "@oppy/omniv2-entity";
import { useToastContext as useToastContext7 } from "@oppy/omniv2-ui";
import { useMutation as useMutation5, useQueryClient as useQueryClient6 } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
var normalizeToRcsUpdatePayload = function(rcsSettings) {
  const { flowDataJson, ...rest } = rcsSettings;
  return {
    ...rest,
    content: "{}",
    flowDataJson: JSON.stringify(flowDataJson)
  };
};
function useUpdateRcsSettings() {
  const { entityId } = useEntity();
  const { rcsSettingsId = "" } = useParams();
  const queryClient = useQueryClient6();
  const navigate = useNavigate();
  const { mutate, ...mutationFlags } = useMutation5({
    mutationFn: updateRcsSettings
  });
  const { showMessage } = useToastContext7();
  const updateRcsSettings2 = (rcsSettings) => {
    const data = normalizeToRcsUpdatePayload(rcsSettings);
    mutate(
      { entityId, rcsSettingsId, data },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["rcsSettings", entityId]
          });
          showMessage({
            severity: "success",
            detail: "Votre contenu RCS a bien \xE9t\xE9 modifi\xE9"
          });
          navigate("..");
        },
        onError: () => {
          showMessage({
            severity: "error",
            summary: "Erreur...",
            detail: "Votre contenu RCS n'a pas pu \xEAtre modifi\xE9"
          });
        }
      }
    );
  };
  return { updateRcsSettings: updateRcsSettings2, ...mutationFlags };
}

export {
  useSearchAdresse,
  useActivateAgent,
  useCreateAgent,
  useGetAgentList,
  useSuspenseGetAgent,
  useCreateRcsSettings,
  useDeleteRcsSettings,
  useGetRcsSettingsList,
  useSuspenseGetRcsSettings,
  useUpdateRcsSettings,
  useGetCampaignStatistics,
  useGetCTAEngagement,
  useGetStatsDeliverability,
  COLUMNS,
  useGetStatsEngagement,
  useGetStatsTableEngagement,
  useGetStatsUnsubscription
};
